import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PrinterConnectionStatusEnum } from '../enums';
import { IPrinterConfig, IPrinterConnectionState, PrinterConnectionStatus, PrinterConnectionType } from '../models';
import { TcpConnectionStrategy } from './strategies/tcp-connection-strategy';
import { WebUSBConnectionStrategy } from './strategies/webusb-connection-strategy';

/**
 * Service responsible for establishing and managing connections to printers
 * Supports: Manual IP, WebUSB, Network Discovery (disabled)
 */
@Injectable({
  providedIn: 'root',
})
export class PrinterConnectionService {
  private readonly translateService = inject(TranslateService);
  private readonly webUsbStrategy = inject(WebUSBConnectionStrategy);
  private readonly tcpStrategy = inject(TcpConnectionStrategy);

  // Connection states for each printer by ID
  private readonly connectionStates = signal<Map<string, IPrinterConnectionState>>(new Map());

  // Feature support flags
  readonly isWebUSBSupported = signal<boolean>(this.webUsbStrategy.isSupported());
  readonly isNetworkDiscoverySupported = signal<boolean>(false); // Direct Sockets API - disabled

  // Computed: Get all connection states as array
  readonly allConnectionStates = computed(() => Array.from(this.connectionStates().values()));

  /**
   * Overall printer health status for display in top bar
   * @see PrinterConnectionStatusEnum
   */
  readonly overallStatus = computed<PrinterConnectionStatusEnum>(() => {
    const states = this.allConnectionStates();
    if (states.length === 0) return PrinterConnectionStatusEnum.UNKNOWN;

    const hasErrors = states.some((s) => s.status === PrinterConnectionStatus.ERROR);
    if (hasErrors) return PrinterConnectionStatusEnum.ERROR;

    const connectedCount = states.filter((s) => s.status === PrinterConnectionStatus.CONNECTED).length;
    if (connectedCount === states.length) return PrinterConnectionStatusEnum.HEALTHY;
    if (connectedCount > 0) return PrinterConnectionStatusEnum.PARTIAL;

    return PrinterConnectionStatusEnum.DISCONNECTED;
  });

  /**
   * Count of printers with errors
   */
  readonly errorCount = computed(() => this.allConnectionStates().filter((s) => s.status === PrinterConnectionStatus.ERROR).length);

  /**
   * Count of connected printers
   */
  readonly connectedCount = computed(() => this.allConnectionStates().filter((s) => s.status === PrinterConnectionStatus.CONNECTED).length);

  /**
   * Total tracked printer count
   */
  readonly totalCount = computed(() => this.allConnectionStates().length);

  constructor() {
    // Listen for USB device disconnection from strategy
    this.webUsbStrategy.onDisconnect.subscribe((printerId) => {
      this.updateConnectionState(printerId, {
        printerId,
        status: PrinterConnectionStatus.DISCONNECTED,
        error: this.translateService.instant('printers.errors.usb_disconnected'),
      });
    });
  }

  /**
   * Get connection state for a specific printer
   */
  getConnectionState(printerId: string): IPrinterConnectionState | undefined {
    return this.connectionStates().get(printerId);
  }

  /**
   * Connect to a printer based on its connection type
   */
  async connect(printer: IPrinterConfig): Promise<boolean> {
    const currentState = this.connectionStates().get(printer.id);
    if (currentState?.status === PrinterConnectionStatus.CONNECTED) {
      return true;
    }

    this.updateConnectionState(printer.id, {
      printerId: printer.id,
      status: PrinterConnectionStatus.CONNECTING,
    });

    try {
      let success = false;
      switch (printer.connectionType) {
        case PrinterConnectionType.WEB_USB:
          success = await this.webUsbStrategy.connect(printer);
          break;

        case PrinterConnectionType.MANUAL_IP:
          success = await this.tcpStrategy.connect(printer);
          break;

        case PrinterConnectionType.NETWORK_DISCOVERY:
          // Network discovery connections are not supported in standard web apps
          this.updateConnectionState(printer.id, {
            printerId: printer.id,
            status: PrinterConnectionStatus.ERROR,
            error: this.translateService.instant('printers.errors.network_discovery_not_supported'),
          });
          return false;

        default:
          throw new Error(this.translateService.instant('printers.errors.unknown_connection_type', { type: printer.connectionType }));
      }

      if (success) {
        this.updateConnectionState(printer.id, {
          printerId: printer.id,
          status: PrinterConnectionStatus.CONNECTED,
          connectedAt: new Date(),
        });
        return true;
      } else {
        throw new Error(this.translateService.instant('printers.errors.connection_failed'));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : this.translateService.instant('printers.errors.connection_failed');
      this.updateConnectionState(printer.id, {
        printerId: printer.id,
        status: PrinterConnectionStatus.ERROR,
        error: errorMessage,
      });
      return false;
    }
  }

  /**
   * Disconnect from a printer
   */
  async disconnect(printerId: string): Promise<void> {
    // Try to disconnect from both strategies as we don't know which one was used without looking up config
    // Or we could store which strategy is active for which printer.
    // For now, calling disconnect on both is safe as they handle non-existent connections gracefully.

    await this.webUsbStrategy.disconnect(printerId);
    await this.tcpStrategy.disconnect(printerId);

    this.updateConnectionState(printerId, {
      printerId,
      status: PrinterConnectionStatus.DISCONNECTED,
    });
  }

  /**
   * Send data to a connected printer
   */
  async sendData(printerId: string, data: Uint8Array): Promise<boolean> {
    const state = this.connectionStates().get(printerId);
    if (state?.status !== PrinterConnectionStatus.CONNECTED) {
      throw new Error(this.translateService.instant('printers.errors.printer_not_connected'));
    }

    if (this.webUsbStrategy.hasConnection(printerId)) {
      return this.webUsbStrategy.sendData(printerId, data);
    }

    if (this.tcpStrategy.hasConnection(printerId)) {
      return this.tcpStrategy.sendData(printerId, data);
    }

    throw new Error(this.translateService.instant('printers.errors.no_active_connection'));
  }

  /**
   * Request WebUSB device access (requires user gesture)
   * Returns the selected device or null if cancelled
   */
  async requestUSBDevice(): Promise<USBDevice | null> {
    return this.webUsbStrategy.requestUSBDevice();
  }

  /**
   * Get list of already-paired USB devices
   */
  async getPairedUSBDevices(): Promise<USBDevice[]> {
    return this.webUsbStrategy.getPairedUSBDevices();
  }

  private updateConnectionState(printerId: string, state: IPrinterConnectionState): void {
    this.connectionStates.update((map) => {
      const newMap = new Map(map);
      newMap.set(printerId, state);
      return newMap;
    });
  }
}
